import { useEffect, useState, useRef } from "react";
import { CircleCheck, CircleAlert, AlertTriangle, ChevronDown } from "lucide-react";
import api from "../../lib/api";

/**
 * DataFreshnessBadge — compact header widget showing the aggregate
 * freshness of every data source feeding the Scorecard. Click to open a
 * detailed popover with per-source `hours_ago` + status pills.
 *
 * States:
 *   fresh    (green)  → every source updated < 24h ago
 *   stale    (amber)  → at least one source 24-72h behind
 *   critical (red)    → at least one source > 72h behind or missing
 *
 * Data source: GET /api/scorecard/data-freshness (see backend route
 * `scorecard_data_freshness.py`). Auto-refreshes every 60 seconds so
 * the badge reacts to an in-flight Nightly Sync trigger.
 */
const STATUS_META = {
  fresh:    { color: "#22c55e", bg: "#22c55e14", label: "Datos frescos",     Icon: CircleCheck  },
  stale:    { color: "#f59e0b", bg: "#f59e0b1a", label: "Datos rezagados",   Icon: CircleAlert  },
  critical: { color: "#ef4444", bg: "#ef44441a", label: "Datos desactualizados", Icon: AlertTriangle },
};

function humanizeHours(h) {
  if (h == null) return "sin datos";
  if (h < 1)  return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 24) return `${Math.round(h)}h`;
  const d = Math.floor(h / 24);
  const rest = Math.round(h - d * 24);
  return rest > 0 ? `${d}d ${rest}h` : `${d}d`;
}

export default function DataFreshnessBadge() {
  const [data, setData]   = useState(null);
  const [open, setOpen]   = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/scorecard/data-freshness");
        if (mounted) setData(r.data);
      } catch (_) { /* silent — badge is non-critical */ }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // Close popover on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="freshness-badge-loading">
        <span className="inline-block w-2 h-2 rounded-full bg-gray-300 animate-pulse" />
        Cargando estado…
      </div>
    );
  }

  const meta = STATUS_META[data.overall_status] || STATUS_META.fresh;
  const { Icon } = meta;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="freshness-badge-toggle"
        className="flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all hover:shadow-sm"
        style={{
          borderColor: meta.color + "55",
          backgroundColor: meta.bg,
          color: meta.color,
        }}
      >
        <Icon size={14} strokeWidth={2.5} />
        <span className="text-xs font-semibold">{meta.label}</span>
        <span className="text-[10px] font-mono opacity-70">
          {data.counts.critical > 0 ? `${data.counts.critical} crítico(s)` :
           data.counts.stale    > 0 ? `${data.counts.stale} rezagado(s)`    :
           `${data.counts.fresh}/${data.sources.length} OK`}
        </span>
        <ChevronDown size={12} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 z-50 w-80 rounded-lg border bg-white shadow-lg"
          style={{ borderColor: "#e5e7eb" }}
          data-testid="freshness-badge-popover"
        >
          <div className="px-4 py-3 border-b" style={{ borderColor: "#f3f4f6" }}>
            <div className="text-sm font-semibold text-gray-800">Frescura de datos</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Fresco &lt; {data.thresholds_h.fresh_lt}h · Rezagado &lt; {data.thresholds_h.stale_lt}h · Crítico ≥ {data.thresholds_h.stale_lt}h
            </div>
          </div>
          <ul className="py-1 max-h-80 overflow-y-auto">
            {data.sources.map((s) => {
              const m = STATUS_META[s.status] || STATUS_META.fresh;
              const SIcon = m.Icon;
              return (
                <li key={s.key}
                    className="flex items-start justify-between gap-3 px-4 py-2 hover:bg-gray-50"
                    data-testid={`freshness-row-${s.key}`}>
                  <div className="flex items-start gap-2 min-w-0">
                    <SIcon size={14} style={{ color: m.color, marginTop: 2 }} strokeWidth={2.5} />
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{s.label}</div>
                      <div className="text-[10px] text-gray-400 truncate">
                        {s.last_sale_date
                          ? `Carga del archivo · última venta ${new Date(s.last_sale_date).toLocaleDateString("es-DO", { day: "2-digit", month: "short", timeZone: "UTC" })}`
                          : s.category}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-semibold" style={{ color: m.color }}>
                      {humanizeHours(s.hours_ago)}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {s.last_updated_at
                        ? new Date(s.last_updated_at).toLocaleString("es-DO",
                            { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                        : "sin registro"}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
